import {
  Box,
  Flex,
  freddyTheme,
  Grid,
  GridItem,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import readPropertyExplanationTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/readPropertyExplanationTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import type { Property } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useRef, useState } from 'react';

import SpecificationLink from './SpecificationLink';

type Props = {
  properties: Property[];
  productTypeId: number;
};

function PropertiesSummaryBlock({ properties, productTypeId }: Props) {
  const { t } = useTranslation('webshop');
  const { trackEvent } = useAnalytics();
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const lastFocusedButtonRef = useRef<HTMLButtonElement | null>(null);

  const renderValue = (value: string): ReactNode => {
    if (value === 'true') {
      return (
        <SpriteIcon name='checkmark' aria-label={t('productPage.yes')} color='sentimentPositive' />
      );
    }

    if (value === 'false') {
      return <SpriteIcon name='minus' aria-label={t('productPage.no')} color='sentimentNegative' />;
    }

    return value;
  };

  const onExplanationClick = (property: Property) => () => {
    if (!property.hasExplanation) {
      return;
    }

    setSelectedProperty(property);
    trackEvent(
      readPropertyExplanationTrigger({
        sub_feature: 'short_specifications',
        property_name: property.displayName || '',
        property_value: property.value || ''
      })
    );
  };

  const getButtonId = (propertyId: string | null) => `specification-info-button-${propertyId}`;

  return (
    <Box>
      <Grid rowGap={{ xs: 0, lg: 200 }} columnGap={{ xs: 0, lg: 300 }}>
        {properties?.map(property => (
          <GridItem key={property.propertyId} column={{ xs: 'span 12', lg: 'span 4' }}>
            <Flex
              direction={{ xs: 'row', lg: 'column' }}
              gap={100}
              py={{ xs: 200, lg: 0 }}
              sx={{
                [freddyTheme.breakpoint.maxWidth('md')]: {
                  '& > *:first-of-type': { flexBasis: '60%' }
                },
                [freddyTheme.breakpoint.maxWidth('lg')]: {
                  '& > *:first-of-type': { flexBasis: '40%' }
                }
              }}
            >
              {property.hasExplanation ? (
                <SpecificationLink
                  aria-label={t('productPage.specificationsInfo', {
                    spec: property.displayName
                  })}
                  id={getButtonId(String(property.propertyId))}
                  type='button'
                  component='button'
                  aria-haspopup='dialog'
                  onClick={e => {
                    e.stopPropagation();
                    lastFocusedButtonRef.current = e.currentTarget;
                    onExplanationClick(property)();
                  }}
                >
                  {property.displayName}
                </SpecificationLink>
              ) : (
                <Typography color='additional'>{property.displayName}</Typography>
              )}
              {renderValue(property.value || '')}
            </Flex>
          </GridItem>
        ))}
      </Grid>
      {selectedProperty && (
        <PropertyExplanationModal
          open={!!selectedProperty}
          onClose={() => {
            setSelectedProperty(null);
            if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
              requestAnimationFrame(() => {
                lastFocusedButtonRef.current?.focus?.();
              });
            } else {
              lastFocusedButtonRef.current?.focus?.();
            }
          }}
          property={{
            ...selectedProperty,
            displayName: selectedProperty.displayName || '',
            hasExplanation: Boolean(selectedProperty.hasExplanation),
            propertyId: String(selectedProperty.propertyId)
          }}
          productTypeId={productTypeId.toString()}
        />
      )}
    </Box>
  );
}

export default PropertiesSummaryBlock;
